import { FC } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import Head from 'next/head';
import { Icon, Page } from 'src/components';
import { ROUTES } from 'src/constants/routes.const';
import useSafePush from 'src/hooks/useSafePush';
import { SummaryItem } from 'src/api/learningSessions/types';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';
import { PathBeforeNavigate, PathContinueButtons } from 'src/apps/Path/PathContinueButtons';
import { useNextPathStep } from 'src/apps/Path/useNextPathStep';
import { ClassicEndOfSessionLearn } from './ClassicEndOfSessionLearn';
import { CongratulationVideoBanner } from './CongratulationVideoBanner';
import { CheckpointProgressCard } from './CheckpointProgressCard';
import * as S from './styles';

const msgs = defineMessages({
  title: { id: 'scenario_end_of_session_title' },
  learningSettings: { id: 'web_ls_menu' },
});

export const EndOfSessionLearn: FC<{
  summaryItems: SummaryItem[];
  pathwayNodeId?: number | null;
  /** Called with whichever exit action ("Continue" or "Go back to my path") the learner
   * picks, letting the caller defer it behind an interstitial instead of running it right
   * away. When omitted, both exits run immediately. */
  onBeforeExit?: PathBeforeNavigate;
}> = ({ summaryItems, pathwayNodeId = null, onBeforeExit }) => {
  const { safePush } = useSafePush();
  const intl = useIntl();
  const languagePairId = getLanguagePairId();
  const {
    isPathEnabled,
    isLoading: isPathLoading,
    model,
  } = useNextPathStep(languagePairId ?? undefined);

  if (isPathLoading) {
    return null;
  }

  if (!isPathEnabled) {
    return (
      <ClassicEndOfSessionLearn
        summaryItems={summaryItems}
        pathwayNodeId={pathwayNodeId}
        onBeforeExit={onBeforeExit}
      />
    );
  }

  return (
    <Page footerStyle="none">
      <Head>
        <title>{intl.formatMessage(msgs.title)} - Memrise</title>
      </Head>

      <S.Root data-testid="scenarios-EndOfSession">
        <S.Content>
          <CongratulationVideoBanner />

          {model && (
            <S.RiseIn>
              <CheckpointProgressCard model={model} />
            </S.RiseIn>
          )}

          <S.ButtonContainer>
            <PathContinueButtons
              languagePairId={languagePairId}
              pathwayNodeId={pathwayNodeId}
              sessionType="vocab"
              onBeforeNavigate={onBeforeExit}
            />
          </S.ButtonContainer>
        </S.Content>
        <S.SettingsIconWrapper
          onClick={async () => {
            await safePush(ROUTES.settings.learning);
          }}
          aria-label={intl.formatMessage(msgs.learningSettings)}
        >
          <Icon name="settingsFilled" />
        </S.SettingsIconWrapper>
      </S.Root>
    </Page>
  );
};
